import { Injectable, inject } from '@angular/core';
import { AuthService, ExternalUserProviderModel } from '@any2any/platform-api';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { AuthState } from '../types/auth-state';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class DisplayAuthService {

  private readonly platformAuthService = inject(AuthService);
  private readonly storageService = inject(StorageService);
  private readonly translateService = inject(TranslateService);

  private readonly stateSubject: BehaviorSubject<AuthState>;

  constructor() {
    const storedState = this.storageService.data.authState;
    this.stateSubject = new BehaviorSubject(storedState ?? { status: 'anonymous' });
    this.bindStateStorage();
  }

  public async checkAuth() {
    switch (this.stateSubject.value.status) {
      case 'anonymous':
        await this.initializeTransaction();
        return false;
      case 'pending':
        return await this.checkTransaction(this.stateSubject.value.transactionId);
      case 'validated':
        return await this.login(this.stateSubject.value.transactionId);
      default:
        return true;
    }
  }

  public get authStatus(): AuthState['status'] {
    return this.stateSubject.value.status;
  }

  public logoutDisplay(): void {
    this.storageService.data.authState = null;
  }

  public logoutAll(): Promise<boolean> {
    this.logoutDisplay();
    return this.platformAuthService.logout();
  }

  private bindStateStorage(): void {
    this.stateSubject.asObservable()
      .subscribe(state => {
        console.debug(`Auth status = ${state.status}`);
        this.storageService.data.authState = state;
      });
  }

  private async initializeTransaction() {
    const provider = new ExternalUserProviderModel();
    provider.providerName = 'non_interactive';
    provider.providerCategory = 'internal';
    const transaction = await this.platformAuthService.createExtAuthTransaction(provider, true);
    if (transaction) {
      console.debug('External auth transaction created');
      this.stateSubject.next({
        status: 'pending',
        transactionId: transaction.transactionId!
      });
      return;
    }
    console.debug('External auth transaction creation error occurred');
  }

  private async checkTransaction(transactionId: string): Promise<boolean> {
    const transaction = await this.platformAuthService.getExtAuthTransaction(transactionId);
    if (transaction) {
      if (transaction.status === 'valid') {
        console.debug('External auth transaction validated');
        this.stateSubject.next({
          status: 'validated',
          transactionId
        });
        return this.login(transactionId);
      }
      return false;
    }
    console.debug(`Couldn't retrieve external auth transaction`);
    return false;
  }

  private async login(transactionId: string): Promise<boolean> {
    const success = await this.platformAuthService.externalLogin(transactionId);
    if (success) {
      console.debug('External auth login successful');
      const device = this.platformAuthService.authInfo.device!;
      this.stateSubject.next({
        status: 'authorized',
        deviceId: device.id!
      });
      return true;
    }
    console.debug(`External auth login failed with message: ${this.translateService.instant(this.platformAuthService.authInfo.errorMsg!)}`);
    return false;
  }

}
